import { useFormik } from "formik"
import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { array, number, object, ObjectSchema, string } from "yup"

import {
    IDealDetailsForm,
    resetProjectFormOnTemplateChange,
    setDealDetailsForm,
} from "components/ProjectForm/ProjectFormSlice"
import EModuleIds, { ESubModuleIds } from "enums/ModuleEnum"
import { DropdownObj } from "pages/Common/commonTypes"
import {
    useGetAllSOWsQuery,
    useGetSOWTemplatesQuery,
    useReconcileTemplateChangesMutation,
} from "pages/Projects/ProjectServices"
import { enqueueSnackbar } from "notistack"
import { TStore } from "store/Store"
import { isTemplateLocked, templateChangeErrorMessage } from "utils/productionMovement"
import { useTemplateUIRules } from "components/ProjectForm/context/TemplateUIRulesContext"
import { ProjectDealDetailsProps } from "."

export enum EPROJECT_FORM_DEAL_DETAILS_FIELDS {
    DEAL_TYPE = "deal_type",
    DEAL_SCOPE_OF_WORK = "deal_scope_of_work",
    TEMPLATE_ID = "template_id",
}

const schema: ObjectSchema<IDealDetailsForm> = object({
    [EPROJECT_FORM_DEAL_DETAILS_FIELDS.DEAL_TYPE]: object({
        id: string().required(),
        title: string().required(),
    }).required(),
    [EPROJECT_FORM_DEAL_DETAILS_FIELDS.DEAL_SCOPE_OF_WORK]: array()
        .of(
            object({
                id: number().required(),
                title: string().required(),
            })
        )
        .required(),
    [EPROJECT_FORM_DEAL_DETAILS_FIELDS.TEMPLATE_ID]: object({
        id: number().required(),
        title: string().required(),
    }).required(),
})

const useProjectFormDealDetails = (props: ProjectDealDetailsProps) => {
    const dispatch = useDispatch()
    const { deal_details_form, project_id } = useSelector((state: TStore) => state.ProjectForm)
    const [isFetchingPages, setIsFetchingPages] = useState(false)
    const [showTemplateChangeModal, setShowTemplateChangeModal] = useState(false)
    const [pendingTemplateId, setPendingTemplateId] = useState<number | null>(null)
    const [reconcileTemplateChanges, { isLoading: isReconcilingTemplate }] = useReconcileTemplateChangesMutation()
    // SB-7554: a project on a Production Planning template keeps it (the backend answers 409), so its cards are
    // disabled up front instead of opening the "will delete all data" warning first.
    const { uiRules } = useTemplateUIRules()
    const templateLocked = isTemplateLocked(project_id, uiRules)

    const formik = useFormik({
        initialValues: deal_details_form,
        onSubmit: handleSubmit,
        validateOnChange: true,
        validationSchema: schema,
        enableReinitialize: true,
    })
    // moduleId stays EModuleIds.LOGISTICS throughout the rest of this form for a Dispatch Request
    // (see ProjectFormPageProps's isDispatchRequest doc) — every other step depends on that to
    // behave like Logistics. The SOW template fetch is the one place that needs to be scoped
    // correctly against the real backend module: SCM (23) / Dispatch Request submodule (24).
    const moduleIdParam = props.isDispatchRequest
        ? { module_id: EModuleIds.SCM, submodule_id: JSON.stringify(ESubModuleIds.DISPATCH_REQUEST) }
        : props.moduleId && !props?.subModuleId && props.moduleId !== EModuleIds.SITES
          ? { module_id: props.moduleId }
          : props.moduleId && props.subModuleId
            ? { module_id: props.moduleId, submodule_id: JSON.stringify(props.subModuleId) }
            : {}
    const { data: allSOWs, isLoading: isAllSOWsLoading } = useGetAllSOWsQuery()
    const { data: sowTemplates } = useGetSOWTemplatesQuery(moduleIdParam)

    const sowTemplateOptions = useMemo(() => {
        return sowTemplates?.filter(
            i => i.is_active === 1 && i.deal_type?.toLowerCase() === formik.values.deal_type?.id?.toLowerCase()
        )
    }, [sowTemplates, formik.values.deal_type])

    const sowOptions = useMemo(() => {
        return allSOWs?.map(sow => ({
            id: sow.sow_id,
            title: sow.sow_name,
        }))
    }, [allSOWs])

    // Auto-select the template when only one is available for the selected deal type
    useEffect(() => {
        if (
            !isAllSOWsLoading &&
            formik.values.deal_type?.title &&
            sowTemplateOptions?.length === 1 &&
            !formik.values.template_id?.id
        ) {
            applyTemplateChange(sowTemplateOptions[0].template_id)
        }
    }, [sowTemplateOptions, isAllSOWsLoading, formik.values.deal_type, formik.values.template_id])

    const handleDealTypeChange = (value: DropdownObj) => {
        if (templateLocked) return
        formik.setFieldValue(EPROJECT_FORM_DEAL_DETAILS_FIELDS.DEAL_TYPE, value)
        formik.setFieldValue(EPROJECT_FORM_DEAL_DETAILS_FIELDS.TEMPLATE_ID, undefined)
        formik.setFieldValue(EPROJECT_FORM_DEAL_DETAILS_FIELDS.DEAL_SCOPE_OF_WORK, [])
    }

    const handleDealTemplateChange = async (id: number) => {
        const currentTemplateId = formik.values.template_id?.id
        if (templateLocked) return

        // If project_id exists and template is being changed, show warning modal
        if (project_id && currentTemplateId && currentTemplateId !== id) {
            setPendingTemplateId(id)
            setShowTemplateChangeModal(true)
            return
        }

        await applyTemplateChange(id)
    }

    const applyTemplateChange = async (id: number) => {
        setIsFetchingPages(true)
        const selectedTemplate = sowTemplates?.find(i => i.template_id === id)
        formik.setFieldValue(EPROJECT_FORM_DEAL_DETAILS_FIELDS.TEMPLATE_ID, {
            id,
            title: selectedTemplate?.template_name,
        })
        if (selectedTemplate) {
            const selectedSOWs = selectedTemplate.scope.map(i => ({
                id: i.sow_id,
                title: i.sow_name,
            }))
            formik.setFieldValue(EPROJECT_FORM_DEAL_DETAILS_FIELDS.DEAL_SCOPE_OF_WORK, selectedSOWs)
            await props.handleGetTemplatePages(id)
        }
        setIsFetchingPages(false)
    }

    const handleConfirmTemplateChange = async () => {
        if (!pendingTemplateId || !project_id || !formik.values.template_id?.id) return

        try {
            await reconcileTemplateChanges({
                old_template_id: formik.values.template_id.id,
                new_template_id: pendingTemplateId,
                project_id: project_id,
            }).unwrap()

            dispatch(resetProjectFormOnTemplateChange())
            await applyTemplateChange(pendingTemplateId)
        } catch (error) {
            console.error("Error reconciling template changes:", error)
            // SB-7554: a Production Planning project keeps its template — say so instead of failing silently.
            enqueueSnackbar(templateChangeErrorMessage(error), { variant: "error" })
        } finally {
            setShowTemplateChangeModal(false)
            setPendingTemplateId(null)
        }
    }

    const handleCancelTemplateChange = () => {
        setShowTemplateChangeModal(false)
        setPendingTemplateId(null)
    }

    function handleSubmit(values: IDealDetailsForm) {
        if (isFetchingPages) return
        dispatch(setDealDetailsForm(values))
        props.handleNextStep()
    }

    return {
        states: {
            formik,
            deal_details_form,
            sowTemplates,
            sowOptions,
            sowTemplateOptions,
            isAllSOWsLoading,
            showTemplateChangeModal,
            isReconcilingTemplate,
            templateLocked,
        },
        functions: {
            dispatch,
            handleSubmit,
            handleDealTemplateChange,
            handleDealTypeChange,
            handleConfirmTemplateChange,
            handleCancelTemplateChange,
        },
    }
}

export default useProjectFormDealDetails
